import { clamp, round0 } from './formatters'
import {
  commissionsModeled,
  managerView,
  profitByMonth,
  upfrontCommissionPerSale,
  upfrontOverridePerSale,
} from './commissions'

export type PackageType = '' | 'advantage'
export type BuildPayType = '' | 'upfront' | 'two'
export type RevenueCadence = 'one-time' | 'monthly' | 'quarterly'
// Pipeline management, charged per $2,500 bucket of monthly ad spend.
export const PIPELINE_BUCKET_FEE = 1000
// Optional corporate recruiter add-on: charged per $2,500 bucket of monthly ad
// spend, plus a one-time addition to the implementation fee.
export const RECRUITER_MONTHLY_FEE = 2000
export const RECRUITER_BUILD_UPCHARGE = 1500
// Milestone bonuses paid per rep still active in the field.
export const BONUS_DAY_15 = 500
export const BONUS_DAY_45 = 1000
export const BONUS_DAY_90 = 1000
// Leader campaign: a search that runs before the standard campaign. It can be
// modeled as a one-month or a two-month search for a single sales leader.
export const LEADER_CAMPAIGN_MAX_MONTHS = 2
// Each search month defaults to $2,500 ad spend. Fees are a flat $1,500
// partnership fee plus pipeline management per $2,500 bucket of that month's
// ad spend — so raising leader spend raises the pipeline fee with it.
export const LEADER_CAMPAIGN_MONTHLY_AD_SPEND = 2500
export const LEADER_PARTNERSHIP_FEE = 1500
export const leaderPipelineFee = (adSpend: number) =>
  adSpend <= 0 ? 0 : Math.ceil(adSpend / 2500) * PIPELINE_BUCKET_FEE
// Ad spend for a given leader search month (0-based).
export const getLeaderAdSpend = (
  inputs: { leaderAdSpend?: number[] },
  monthIdx: number,
): number => {
  const value = inputs.leaderAdSpend?.[monthIdx]
  return value !== undefined && value !== null && !Number.isNaN(value)
    ? Math.max(0, value)
    : LEADER_CAMPAIGN_MONTHLY_AD_SPEND
}
// Average ad spend cost per hire shown for leader search months.
export const LEADER_CAMPAIGN_CPH = 2500
// The leader earns the same milestone bonuses as every rep: $500 / $1,000 /
// $1,000 across their first 90 days.
export const LEADER_CAMPAIGN_HIRES = 1
export const LEADER_CAMPAIGN_BUILD_UPCHARGE = 1000
// Two fee schedules. "monthly" is the default: no implementation fee, a
// $2,500 partnership, $1,250 pipeline + $2,000 recruiter per $2,500 bucket,
// the one-time implementation fee and a $500 placement bonus at Day 15.
// "legacy" (Flagship) adds a $1,000 retention bonus at Day 45.
export type PricingModel = 'monthly' | 'legacy'
export interface PricingSchedule {
  implementationFee: number
  implementationSplitPayment: number
  leaderBuildUpcharge: number
  recruiterBuildUpcharge: number
  partnershipFee: number
  leaderPartnershipFee: number
  pipelineBucketFee: number
  recruiterBucketFee: number
  bonusDay15: number
  bonusDay45: number
  bonusDay90: number
  // Collect the first month's monthly fees in the Build column. On for both
  // schedules: the Build row is "signing", and the first month's fees (the
  // first leader search month, or campaign Month 1) are due then, so the
  // model matches the proposal's amount due at signing.
  firstMonthInBuild: boolean
}
export const PRICING_SCHEDULES: Record<PricingModel, PricingSchedule> = {
  // Default: implementation fee, monthly service and the $500 placement bonus.
  monthly: {
    implementationFee: 4500,
    implementationSplitPayment: 1500,
    leaderBuildUpcharge: LEADER_CAMPAIGN_BUILD_UPCHARGE,
    recruiterBuildUpcharge: RECRUITER_BUILD_UPCHARGE,
    partnershipFee: 2500,
    leaderPartnershipFee: 2500,
    pipelineBucketFee: 1250,
    recruiterBucketFee: RECRUITER_MONTHLY_FEE,
    bonusDay15: 500,
    bonusDay45: 0,
    bonusDay90: 0,
    firstMonthInBuild: true,
  },
  legacy: {
    implementationFee: 4500,
    implementationSplitPayment: 1500,
    leaderBuildUpcharge: LEADER_CAMPAIGN_BUILD_UPCHARGE,
    recruiterBuildUpcharge: RECRUITER_BUILD_UPCHARGE,
    // Flagship: identical to the default, plus a $1,000 retention bonus at
    // day 45.
    partnershipFee: 2500,
    leaderPartnershipFee: 2500,
    pipelineBucketFee: 1250,
    recruiterBucketFee: RECRUITER_MONTHLY_FEE,
    bonusDay15: 500,
    bonusDay45: 1000,
    bonusDay90: 0,
    firstMonthInBuild: true,
  },
}

// Share of launched reps still active at each bonus milestone. There's no
// retention input for day 15 or day 45, so each is estimated halfway between
// the rates on either side: day 15 sits halfway between launch (100%) and the
// day-30 rate; day 45 halfway between the day-30 and day-60 rates. Counts are
// rounded up to whole reps wherever they're billed.
export const day15Share = (pct30: number) => (1 + pct30) / 2
export const day45Share = (pct30: number, pct60: number) =>
  (pct30 * (1 + pct60)) / 2

// Under the doubled-attrition stress case half as many reps survive each
// stage, so the estimated bonuses on them drop by the same share.
export const DOUBLE_ATTRITION_SURVIVAL = 0.5

// The leader sells a third of a veteran's monthly output in the month they're
// hired, then a full veteran's rate for the rest of the first year. In year
// two they step back from selling to manage the larger team.
export const LEADER_HIRE_MONTH_SALES_SHARE = 0.33
export const LEADER_SELLING_MONTHS = 12

/** Column label for model month idx (0-based). Months count straight from
 *  signing; leader search months are tagged "(leader)", so after a two-month
 *  search the rep campaign starts in Month 3. */
export const periodLabel = (idx: number, leaderMonths: number) =>
  idx < leaderMonths ? `Month ${idx + 1} (leader)` : `Month ${idx + 1}`
export const getPricing = (inputs: {
  pricingModel?: PricingModel
  noImplementationFee?: boolean
}): PricingSchedule => {
  const schedule = PRICING_SCHEDULES[inputs.pricingModel ?? 'monthly']
  // The proposal can switch the implementation fee off entirely.
  return inputs.noImplementationFee
    ? {
        ...schedule,
        implementationFee: 0,
        implementationSplitPayment: 0,
        leaderBuildUpcharge: 0,
        recruiterBuildUpcharge: 0,
      }
    : schedule
}
// Pipeline management for a leader search month at the active schedule.
export const leaderPipelineFeeFor = (
  pricing: PricingSchedule,
  adSpend: number,
) => (adSpend <= 0 ? 0 : Math.ceil(adSpend / 2500) * pricing.pipelineBucketFee)
// How many months the leader search is modeled at (0 = off).
export const getLeaderMonths = (inputs: {
  leaderCampaignMonths?: number
}): number =>
  Math.min(LEADER_CAMPAIGN_MAX_MONTHS, Math.max(0, inputs.leaderCampaignMonths ?? 0))
export type IndustryType =
  | ''
  | 'Windows'
  | 'Roofing'
  | 'Solar (Sales)'
  | 'Solar (Vertical)'
  | 'Solar (Community)'
  | 'Pest'
  | 'Summer Pest'
  | 'Fiber'
  | 'Deregulated Energy'
  | 'Hardscaping'
  | 'Landscaping'
  | 'Other'

export interface Inputs {
  companyName: string
  pkg: PackageType
  buildPay: BuildPayType
  industry: IndustryType
  campaigns: number
  monthlyCampaigns?: number[] // Array of 12 numbers for monthly overrides
  adSpend: number
  monthlyAdSpend?: number[] // Array of 12 numbers for monthly ad spend overrides
  cph: number
  ticket: number
  gpm: number // 0-100 in state, converted to decimal for calc
  salesVetMonthly: number
  salesFirst90Total: number
  pctLaunched: number // 0-100
  pct30: number // 0-100
  pct90: number // 0-100 Day 30 to Day 90 retention
  pct1y: number // 0-100
  pullthrough: number // 0-100
  revenueCadence: RevenueCadence // New: one-time, monthly, or quarterly
  residualTicket?: number // New: monthly ticket for residual products
  monthlyAttrition?: number // New: monthly customer attrition (0-100)
  quarterlyAttrition?: number // New: quarterly customer attrition (0-100)
  // Residual products only. When either commission % is set, gpm means
  // margin before commissions and commissions are modeled as cash flow.
  commissionPct?: number // Upfront commission, % of first-year ACV, paid at sale
  clawbackMonths?: number // Upfront commission recovered if the customer cancels within this many months
  residualCommissionPct?: number // % of each recurring payment paid as commission
  backendCommissionPct?: number // % of first-year ACV paid when the clawback window closes, on surviving customers
  modelPerspective?: 'company' | 'manager' // Residual products: company gross profit, or a manager's override income
  overrideUpfrontPct?: number // Manager: % of first-year ACV paid at sale
  overrideBackendPct?: number // Manager: % of first-year ACV on accounts in good standing, net of clawbacks
  overrideBonusMonth?: number // Manager: month of the year (1–12) the backend is paid; 0 = rolling
  monthlyBuildFees?: number[] // Array of 13 numbers for build fee overrides (Build + 12 months)
  // A ramping rep's output in each of their first three producing months,
  // as a percentage of a veteran's monthly sales.
  rampMonth1Pct?: number
  rampMonth2Pct?: number
  rampMonth3Pct?: number
  leaderCampaignMonths?: number // 0 = off, 1 or 2 = months spent finding the sales leader
  leaderAdSpend?: number[] // Ad spend per leader search month (defaults to $2,500 each)
  leaderMonth2Sales?: number // Optional override for total sales in the month the leader is hired
  pausedSalesMonths?: number[] // Model months (0-based, after Implementation) with no new sales; reps stay and keep attriting
  launchMonth?: number // 1–12 (from signing): reps hired earlier start their Month 1 sales here; 0 = off
  revenueDelayMonths?: number // 0–11: months between a sale and cash collection (revenue and profit lag; sales don't)
  summerProgram?: boolean // Summer program preset is on (ads paused Months 6–12, sales paused 1–3 and 10–12)
  summerRestore?: Record<number, [number, number]> // Per-campaign spend and tier the preset paused, restored when it's turned off
  excludeLeaderSales?: boolean // Internal: run the model without the leader's sales (to split them out)
  addRecruiter?: boolean // Adds the corporate recruiter monthly fee
  cphLow?: number // Low end of the ad spend cost-per-hire range
  cphHigh?: number // High end of the ad spend cost-per-hire range
  salesSchoolEnabled?: boolean // Enables milestone bonus charges
  pricingModel?: PricingModel // Fee schedule; defaults to 'monthly'
  noImplementationFee?: boolean // Proposal switch: no implementation fee
  revenueTarget?: number // 12-month gross revenue target for optimizer
}

export interface IndustryDefaults {
  ticket: number
  gpm: number
  salesFirst90Total: number
  salesVetMonthly: number
  revenueCadence: RevenueCadence
  residualTicket?: number
  pullthrough?: number // falls back to the general default when not set
}

export const INDUSTRY_DEFAULTS: Record<IndustryType, IndustryDefaults> = {
  '': {
    ticket: 20000,
    gpm: 30,
    salesFirst90Total: 10,
    salesVetMonthly: 6,
    revenueCadence: 'one-time',
  },
  Windows: {
    ticket: 20000,
    gpm: 30,
    salesFirst90Total: 10,
    salesVetMonthly: 6,
    revenueCadence: 'one-time',
  },
  Roofing: {
    ticket: 20000,
    gpm: 20,
    salesFirst90Total: 10,
    salesVetMonthly: 6,
    revenueCadence: 'one-time',
  },
  'Solar (Sales)': {
    ticket: 40000,
    gpm: 10,
    salesFirst90Total: 6,
    salesVetMonthly: 6,
    revenueCadence: 'one-time',
  },
  'Solar (Vertical)': {
    ticket: 40000,
    gpm: 20,
    salesFirst90Total: 6,
    salesVetMonthly: 6,
    revenueCadence: 'one-time',
  },
  'Solar (Community)': {
    ticket: 700,
    gpm: 50,
    salesFirst90Total: 60,
    salesVetMonthly: 45,
    revenueCadence: 'monthly',
    residualTicket: 350,
  },
  Pest: {
    ticket: 170,
    gpm: 40,
    salesFirst90Total: 60,
    salesVetMonthly: 45,
    revenueCadence: 'monthly',
    residualTicket: 85,
    pullthrough: 90,
  },
  'Summer Pest': {
    ticket: 170,
    gpm: 40,
    salesFirst90Total: 100,
    salesVetMonthly: 50,
    revenueCadence: 'monthly',
    residualTicket: 85,
    pullthrough: 90,
  },
  Fiber: {
    ticket: 100,
    gpm: 50,
    salesFirst90Total: 60,
    salesVetMonthly: 45,
    revenueCadence: 'monthly',
    residualTicket: 50,
  },
  'Deregulated Energy': {
    ticket: 70,
    gpm: 50,
    salesFirst90Total: 60,
    salesVetMonthly: 45,
    revenueCadence: 'monthly',
    residualTicket: 35,
  },
  Hardscaping: {
    ticket: 20000,
    gpm: 25,
    salesFirst90Total: 10,
    salesVetMonthly: 6,
    revenueCadence: 'one-time',
  },
  Landscaping: {
    ticket: 50000,
    gpm: 30,
    salesFirst90Total: 5,
    salesVetMonthly: 8,
    revenueCadence: 'one-time',
  },
  Other: {
    ticket: 25000,
    gpm: 20,
    salesFirst90Total: 10,
    salesVetMonthly: 6,
    revenueCadence: 'one-time',
  },
}

export interface PackagePreset {
  name: string
  buildTwoPayments: number
  buildUpfront: number
  systemMonthly: number
  campaignMonthly: number
  recruiterMonthly: number
  bonusLaunch: number
  bonusDay30: number
  bonusDay60: number
  bonusDay90: number
}

export const PACKAGE_PRESETS: Record<PackageType, PackagePreset> = {
  '': {
    name: 'No Package Selected',
    buildTwoPayments: 0,
    buildUpfront: 0,
    systemMonthly: 0,
    campaignMonthly: 0,
    recruiterMonthly: 0,
    bonusLaunch: 0,
    bonusDay30: 0,
    bonusDay60: 0,
    bonusDay90: 0,
  },
  advantage: {
    name: 'D2D HIRE Advantage',
    // $4,500 implementation fee, splittable into three even payments.
    buildTwoPayments: 1500,
    buildUpfront: 4500,
    // Flat monthly partnership fee.
    systemMonthly: 1500,
    // Pipeline management, charged per $2,500 bucket of monthly ad spend.
    campaignMonthly: PIPELINE_BUCKET_FEE,
    recruiterMonthly: 0,
    bonusLaunch: 150,
    bonusDay30: 0,
    bonusDay60: 0,
    bonusDay90: 0,
  },
}

export interface RowData {
  period: string
  activeCampaigns: number
  hires: number
  launched: number
  // Share of this month's launches counted as active (default 0.5, half a
  // month on average). A stacked summer launch counts the day-15 bonus reps.
  launchWeight?: number
  active30d: number
  active60d: number
  active90d: number
  veterans: number
  rampingReps: number
  rampingSales: number
  veteranReps: number
  buildFee: number
  platformFee: number
  campaignFee: number
  recruiterFee: number
  leaderRevShare: number
  hireCost: number
  adSpend: number
  bonuses: number
  totalCost: number
  totalSales: number
  realizationRate: number
  grossRevenue: number
  grossProfit: number
  netProfit: number
  cumProfit: number
  residualRevenue?: number
  totalResidualRevenue?: number
  /** Net commission cost (upfront + residual − clawbacks) taken out of gross
   *  profit, when commissions are modeled for a residual product. */
  commissions?: number
  commissionUpfront?: number
  commissionResidual?: number
  commissionClawback?: number
  commissionBackend?: number
  /** Manager view: override income split, by payout month. */
  overrideUpfront?: number
  overrideBackend?: number
  overrideClawback?: number
}

export interface CalculationResult {
  preset: PackagePreset
  hiresPerMonth: number
  launchedPerMonth: number
  gpPerSale: number
  totalHireCost: number
  totalAdSpend: number
  totalBonuses: number
  totalCostAll: number
  totalGrossProfit: number
  totalNetProfit: number
  roi: number
  breakeven: string
  rows: RowData[]
}

// The projection engine models two full years. Campaign/ad-spend plans are
// still authored for the first 12 months and repeat into year two.
export const MODEL_MONTHS = 36
export const INPUT_MONTHS = 12
// Selectable projection windows. 36 months is offered for pest only.
export type Timeframe = 3 | 6 | 12 | 24 | 36

// Pest and Summer Pest model years two and three as a revenue trail: no new
// ads, hires or sales after Month 12, only the year-one customers who stay,
// attriting at the selected cadence. That makes the 24- and 36-month views a
// clean return case on what was sold in year one.
export const YEAR_ONE_MONTHS = 12
export const usesRevenueTrail = (industry: IndustryType | string) =>
  industry === 'Pest' || industry === 'Summer Pest'
// A rep's first three producing months, expressed as a percentage of a
// veteran's monthly sales.
export const DEFAULT_RAMP_PCTS: [number, number, number] = [25, 50, 75]
export const getRampShares = (inputs: {
  rampMonth1Pct?: number
  rampMonth2Pct?: number
  rampMonth3Pct?: number
}): [number, number, number] => [
  Math.max(0, inputs.rampMonth1Pct ?? DEFAULT_RAMP_PCTS[0]) / 100,
  Math.max(0, inputs.rampMonth2Pct ?? DEFAULT_RAMP_PCTS[1]) / 100,
  Math.max(0, inputs.rampMonth3Pct ?? DEFAULT_RAMP_PCTS[2]) / 100,
]
// Setting the cumulative first-90-days figure rescales the ramp curve, keeping
// the relative shape of the three months intact.
export function withCumulativeRampSales<T extends {
  salesVetMonthly: number
  rampMonth1Pct?: number
  rampMonth2Pct?: number
  rampMonth3Pct?: number
}>(inputs: T, cumulative: number): T {
  const vet = Math.max(0, inputs.salesVetMonthly)
  const target = Math.max(0, cumulative)
  const shares = getRampShares(inputs)
  const shareTotal = shares[0] + shares[1] + shares[2]
  // Without a veteran rate there is nothing to express the ramp against.
  if (vet <= 0) return inputs
  const shape =
    shareTotal > 0
      ? shares
      : (DEFAULT_RAMP_PCTS.map((pct) => pct / 100) as [
          number,
          number,
          number,
        ])
  const shapeTotal = shape[0] + shape[1] + shape[2]
  const factor = target / (vet * shapeTotal)
  const round1 = (value: number) => Math.round(value * 1000) / 10
  return {
    ...inputs,
    rampMonth1Pct: round1(shape[0] * factor),
    rampMonth2Pct: round1(shape[1] * factor),
    rampMonth3Pct: round1(shape[2] * factor),
  }
}

// The familiar "sales per 90 day rep" figure, now derived from the ramp.
export const salesPerNinetyDayRep = (inputs: {
  salesVetMonthly: number
  rampMonth1Pct?: number
  rampMonth2Pct?: number
  rampMonth3Pct?: number
}): number => {
  const shares = getRampShares(inputs)
  return (
    Math.max(0, inputs.salesVetMonthly) *
    (shares[0] + shares[1] + shares[2])
  )
}
const extendPlan = (plan: number[], length: number): number[] =>
  Array.from(
    { length },
    (_, i) => plan[Math.min(i, plan.length - 1)] ?? plan[plan.length - 1] ?? 0,
  )

export const DEFAULT_INPUTS: Inputs = {
  companyName: '',
  pkg: 'advantage',
  buildPay: 'upfront',
  industry: '',
  campaigns: 2,
  monthlyCampaigns: Array(12).fill(2),
  adSpend: 2500,
  monthlyAdSpend: Array(12).fill(2500),
  cph: 350,
  ticket: 20000,
  gpm: 30,
  salesVetMonthly: 6,
  salesFirst90Total: 10,
  pctLaunched: 70,
  pct30: 40,
  pct90: 40,
  pct1y: 40,
  pullthrough: 80,
  revenueCadence: 'one-time',
  residualTicket: undefined,
  monthlyAttrition: 2,
  quarterlyAttrition: 5,
  monthlyBuildFees: undefined,
  rampMonth1Pct: DEFAULT_RAMP_PCTS[0],
  rampMonth2Pct: DEFAULT_RAMP_PCTS[1],
  rampMonth3Pct: DEFAULT_RAMP_PCTS[2],
  leaderCampaignMonths: 0,
  addRecruiter: false,
  salesSchoolEnabled: true,
  revenueTarget: 0,
}

export function calculateModel(inputs: Inputs): CalculationResult {
  // The model always runs two years; the table decides how much to show.
  const months = MODEL_MONTHS
  const preset = PACKAGE_PRESETS[inputs.pkg]

  const gpm = clamp(inputs.gpm / 100, 0, 1)
  const pctLaunched = clamp(inputs.pctLaunched / 100, 0, 1)
  const pct30 = clamp(inputs.pct30 / 100, 0, 1)
  const pct90 = clamp(inputs.pct90 / 100, 0, 1)
  const pct1y = clamp(inputs.pct1y / 100, 0, 1)
  const pullthrough = clamp(inputs.pullthrough / 100, 0, 1)

  const pct60 = Math.sqrt(pct90)

  // Campaign plans are edited for the first 12 months; year two repeats the
  // last planned month so the second year keeps running at the same pace.
  const monthlyCampaigns = extendPlan(
    inputs.monthlyCampaigns && inputs.monthlyCampaigns.length === INPUT_MONTHS
      ? inputs.monthlyCampaigns
      : Array(INPUT_MONTHS).fill(Math.max(1, inputs.campaigns)),
    months,
  )

  const monthlyAdSpendValues = extendPlan(
    inputs.monthlyAdSpend && inputs.monthlyAdSpend.length === INPUT_MONTHS
      ? inputs.monthlyAdSpend
      : Array(INPUT_MONTHS).fill(Math.max(0, inputs.adSpend)),
    months,
  )

  const cph = Math.max(1, inputs.cph)

  // Leader campaign: a one- or two-month search that delays the standard
  // campaign by the same number of months. Search months charge a flat monthly
  // fee, skip Sales School, and place a single leader rep on a fixed curve.
  const leaderMonths = getLeaderMonths(inputs)
  const leaderCampaign = leaderMonths > 0
  // The leader is hired in the final month of the search.
  const leaderHireIdx = leaderMonths - 1
  const leaderPrefix = Array(leaderMonths).fill(0)
  const effectiveCampaigns = leaderCampaign
    ? [...leaderPrefix, ...monthlyCampaigns.slice(0, months - leaderMonths)]
    : monthlyCampaigns
  const effectiveAdSpendPerCampaign = leaderCampaign
    ? [...leaderPrefix, ...monthlyAdSpendValues.slice(0, months - leaderMonths)]
    : monthlyAdSpendValues

  // Revenue trail: nothing new is bought after year one.
  const revenueTrail = usesRevenueTrail(inputs.industry)
  const isTrailMonth = (m: number) => revenueTrail && m >= YEAR_ONE_MONTHS
  if (revenueTrail) {
    for (let m = YEAR_ONE_MONTHS; m < months; m++) {
      effectiveCampaigns[m] = 0
      effectiveAdSpendPerCampaign[m] = 0
    }
  }

  const hired = effectiveCampaigns.map((campaigns, idx) => {
    const adSpendPerCampaign = effectiveAdSpendPerCampaign[idx]
    const totalAdSpend = adSpendPerCampaign * campaigns
    return round0(totalAdSpend / cph)
  })
  if (leaderCampaign) {
    hired[leaderHireIdx] = LEADER_CAMPAIGN_HIRES
  }

  // The leader rep is tracked separately from the standard funnel.
  const launched = hired.map((h, idx) =>
    leaderCampaign && idx < leaderMonths ? 0 : h * pctLaunched,
  )

  // Launch month (1–12 from signing; 0 = off).
  const launchIdx =
    Number(inputs.launchMonth) >= 1
      ? Math.min(months - 1, Math.round(Number(inputs.launchMonth)) - 1)
      : 0
  // Summer program with a launch month: everyone hired before launch waits,
  // and the share who launch all start together in the launch month. From
  // there they follow the usual retention curve, like any other cohort.
  const stackAtLaunch = !!inputs.summerProgram && launchIdx > 0
  if (stackAtLaunch) {
    let waiting = 0
    for (let c = 0; c < launchIdx; c++) {
      waiting += launched[c]
      launched[c] = 0
    }
    launched[launchIdx] += waiting
  }

  const cohortDay30 = launched.map((x) => x * pct30)
  const cohortDay60 = cohortDay30.map((x) => x * pct60)
  const cohortDay90 = cohortDay60.map((x) => x * pct60)

  const active30d = Array.from({ length: months }, (_, m) => {
    return m >= 1 ? cohortDay30[m - 1] : 0
  })

  const active60d = Array.from({ length: months }, (_, m) => {
    return m >= 2 ? cohortDay60[m - 2] : 0
  })

  const active90d = Array.from({ length: months }, (_, m) => {
    return m >= 3 ? cohortDay90[m - 3] : 0
  })

  const monthlyVeteranRetentionRate = Math.pow(pct1y, 1 / 9)

  const veterans: number[] = []
  for (let m = 0; m < months; m++) {
    if (m <= 3) {
      veterans.push(0)
    } else {
      const previousVeterans = veterans[m - 1] * monthlyVeteranRetentionRate
      const newGraduates = m >= 4 ? cohortDay90[m - 4] : 0
      veterans.push(previousVeterans + newGraduates)
    }
  }

  // The leader rep launches in their hire month and then ages through the
  // retention stages: 30d+ one month later, then 60d+, then 90d+.
  const leaderAt = (m: number, offset: number) =>
    leaderCampaign && m === leaderHireIdx + offset ? LEADER_CAMPAIGN_HIRES : 0
  const leaderLaunched = Array.from({ length: months }, (_, m) =>
    leaderAt(m, 0),
  )
  const leaderActive30d = Array.from({ length: months }, (_, m) =>
    leaderAt(m, 1),
  )
  const leaderActive60d = Array.from({ length: months }, (_, m) =>
    leaderAt(m, 2),
  )
  const leaderActive90d = Array.from({ length: months }, (_, m) =>
    leaderAt(m, 3),
  )
  // After day 90 the leader stays on the team as a veteran for the rest of
  // the first year, then steps back from selling in year two.
  const leaderVeterans = Array.from({ length: months }, (_, m) =>
    leaderCampaign && m >= leaderHireIdx + 4 && m < LEADER_SELLING_MONTHS
      ? LEADER_CAMPAIGN_HIRES
      : 0,
  )
  // The leader sells a third of a veteran's output in their hire month and a
  // full veteran's output every month after that through the first year.
  const leaderSalesMultiplier = (m: number) => {
    if (!leaderCampaign || inputs.excludeLeaderSales) return 0
    if (m === leaderHireIdx) return LEADER_HIRE_MONTH_SALES_SHARE
    return m > leaderHireIdx && m < LEADER_SELLING_MONTHS ? 1 : 0
  }


  // Each ramping rep sells a share of a veteran's monthly output, stepping up
  // across their first three months.
  const vetMonthly = Math.max(0, inputs.salesVetMonthly)
  const rampShare = getRampShares(inputs)

  // Without the summer program, a launch month only delays the first sales of
  // reps hired before it. With it, those reps are already stacked into the
  // launch-month cohort above, so every cohort produces from its own month.
  const productionStart = (cohortMonth: number) =>
    stackAtLaunch ? cohortMonth : Math.max(cohortMonth, launchIdx)

  // Rep counts that actually drive sales, tracked so the UI can explain the math.
  const rampingReps = Array.from({ length: months }, (_, m) => {
    let reps = 0
    for (let c = 0; c <= m; c++) {
      const age = m - productionStart(c)
      if (age >= 0 && age <= 2) reps += cohortDay90[c]
    }
    return reps
  })

  // Sales produced by reps in months 1, 2 and 3 of production.
  const rampingSales = Array.from({ length: months }, (_, m) => {
    let sales = 0
    for (let c = 0; c <= m; c++) {
      const age = m - productionStart(c)
      if (age >= 0 && age <= 2) sales += cohortDay90[c] * rampShare[age]
    }
    return sales * vetMonthly
  })

  const veteranReps = Array.from({ length: months }, (_, m) => {
    let reps = 0
    for (let cohortMonth = 0; cohortMonth <= m; cohortMonth++) {
      const monthsPast90 = m - productionStart(cohortMonth) - 3
      if (monthsPast90 < 0) continue
      reps +=
        cohortDay90[cohortMonth] *
        Math.pow(monthlyVeteranRetentionRate, monthsPast90)
    }
    // The leader rep sells at a fraction of a veteran's rate while active.
    return reps + leaderSalesMultiplier(m)
  })

  const pausedSales = new Set(inputs.pausedSalesMonths || [])
  const totalSales = Array.from({ length: months }, (_, m) => {
    // A paused month closes nothing; the team and its attrition carry on.
    if (pausedSales.has(m)) return 0
    // Revenue trail: only year-one sales are counted.
    if (isTrailMonth(m)) return 0
    // The leader's hire month can be overridden directly.
    if (
      leaderCampaign &&
      !inputs.excludeLeaderSales &&
      m === leaderHireIdx &&
      inputs.leaderMonth2Sales !== undefined &&
      inputs.leaderMonth2Sales !== null
    ) {
      return Math.max(0, inputs.leaderMonth2Sales)
    }
    return rampingSales[m] + veteranReps[m] * vetMonthly
  })

  const realizedSales = totalSales.map((x) => x * pullthrough)

  const residualRevenue: number[] = []
  const totalResidualRevenue: number[] = []

  if (inputs.revenueCadence === 'monthly' && inputs.residualTicket) {
    const monthlyAttritionRate = clamp(
      (inputs.monthlyAttrition || 2) / 100,
      0,
      1,
    )
    const customerRetention = 1 - monthlyAttritionRate

    const newMonthlyRevenue = realizedSales.map(
      (sales) => sales * Math.max(0, inputs.ticket),
    )

    for (let m = 0; m < months; m++) {
      let monthlyResidual = 0

      if (m >= 1) {
        for (let sourceMonth = 0; sourceMonth <= m - 1; sourceMonth++) {
          const monthsGeneratingResidual = m - sourceMonth - 1
          const retentionFactor = Math.pow(
            customerRetention,
            monthsGeneratingResidual,
          )
          monthlyResidual +=
            (newMonthlyRevenue[sourceMonth] / Math.max(0, inputs.ticket)) *
            retentionFactor *
            inputs.residualTicket
        }
      }

      residualRevenue.push(monthlyResidual)
      totalResidualRevenue.push(
        realizedSales[m] * Math.max(0, inputs.ticket) + monthlyResidual,
      )
    }
  } else if (inputs.revenueCadence === 'quarterly' && inputs.residualTicket) {
    const quarterlyAttritionRate = clamp(
      (inputs.quarterlyAttrition || 5) / 100,
      0,
      1,
    )
    const quarterlyRetention = 1 - quarterlyAttritionRate

    const newMonthlyRevenue = realizedSales.map(
      (sales) => sales * Math.max(0, inputs.ticket),
    )

    for (let m = 0; m < months; m++) {
      let quarterlyResidual = 0

      if (m >= 3) {
        const sourceMonth = m - 3
        quarterlyResidual =
          (newMonthlyRevenue[sourceMonth] / Math.max(0, inputs.ticket)) *
          quarterlyRetention *
          inputs.residualTicket
      }

      residualRevenue.push(quarterlyResidual)
      totalResidualRevenue.push(
        realizedSales[m] * Math.max(0, inputs.ticket) + quarterlyResidual,
      )
    }
  } else {
    for (let m = 0; m < months; m++) {
      residualRevenue.push(0)
      totalResidualRevenue.push(realizedSales[m] * Math.max(0, inputs.ticket))
    }
  }

  const grossRevenue = totalResidualRevenue
  // Residual products can model commissions as cash flow: upfront on
  // first-year ACV at sale, clawed back on early cancels, plus any residual
  // commission. Otherwise commissions are assumed to be inside the margin.
  // In the manager view, "gross profit" is the manager's override income.
  const profitRaw = profitByMonth(
    inputs,
    realizedSales,
    residualRevenue,
    grossRevenue,
    gpm,
  )
  // Revenue delay: models the gap between a sale and cash collection. Sales
  // still happen in their own month; revenue, commissions and profit are
  // collected N months later. Costs stay where they are.
  const delay = getRevenueDelay(inputs)
  const lag = (arr: number[]): number[] =>
    delay > 0 ? arr.map((_, i) => (i >= delay ? arr[i - delay] : 0)) : arr
  const lagAll = <T extends object>(obj: T): T =>
    Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [
        k,
        Array.isArray(v) ? lag(v as number[]) : v,
      ]),
    ) as T
  const grossProfit = lag(profitRaw.profit)
  const commissions = lagAll(profitRaw.commissions)
  const overrides = profitRaw.overrides
    ? lagAll(profitRaw.overrides)
    : profitRaw.overrides
  const salesShown = totalSales
  const revenueShown = lag(grossRevenue)
  const residualShown = lag(residualRevenue)
  const totalResidualShown = lag(totalResidualRevenue)
  const gpPerSale = managerView(inputs)
    ? upfrontOverridePerSale(inputs)
    : Math.max(0, inputs.ticket) * gpm -
      upfrontCommissionPerSale(inputs) * (commissionsModeled(inputs) ? 1 : 0)

  // All fee amounts come from the active pricing schedule.
  const pricing = getPricing(inputs)
  // Implementation is a flat one-time fee regardless of ad spend.
  const defaultBuildLaunch = inputs.pkg
    ? pricing.implementationFee +
      (leaderCampaign ? pricing.leaderBuildUpcharge : 0) +
      (inputs.addRecruiter ? pricing.recruiterBuildUpcharge : 0)
    : 0

  // Check for custom build fees - but ignore them entirely when no package is selected
  // (and on schedules without an implementation fee).
  const customBuildFees =
    inputs.pkg && pricing.implementationFee > 0
      ? inputs.monthlyBuildFees
      : undefined

  // Build row fee - use custom if set, otherwise use default
  const buildRowFee =
    customBuildFees &&
    customBuildFees[0] !== undefined &&
    customBuildFees[0] !== null
      ? customBuildFees[0]
      : defaultBuildLaunch

  const systemMonthly = inputs.pkg ? pricing.partnershipFee : 0
  // The service fee scales with each $2,500 bucket of monthly ad spend.
  // Leader search months bill pipeline on the leader's own ad spend.
  const isLeaderMonth = (idx: number) => leaderCampaign && idx < leaderMonths
  const campaignFees = effectiveCampaigns.map((buckets, idx) =>
    !inputs.pkg
      ? 0
      : isLeaderMonth(idx)
        ? leaderPipelineFeeFor(pricing, getLeaderAdSpend(inputs, idx))
        : pricing.pipelineBucketFee * buckets,
  )
  // An optional corporate recruiter is billed per bucket as a monthly add-on.
  const recruiterFees = effectiveCampaigns.map((buckets, idx) =>
    inputs.addRecruiter && inputs.pkg && !isLeaderMonth(idx)
      ? pricing.recruiterBucketFee * buckets
      : 0,
  )

  // Milestone bonuses are earned on reps active at day 15, 45 and 90. There's
  // no retention input for day 15 or 45, so each count is estimated halfway
  // between the rates on either side (see day15Share / day45Share) and
  // rounded up to whole reps, since nobody pays a partial bonus. The proposal
  // documents describe it the same way.
  const bonuses = Array.from({ length: months }, (_, m) => {
    if (!inputs.pkg || inputs.salesSchoolEnabled === false) return 0
    // No bonuses during the leader campaign months.
    if (isLeaderMonth(m)) return 0
    const day15 =
      Math.ceil(launched[m] * day15Share(pct30)) * pricing.bonusDay15
    const day45 =
      m >= 1
        ? Math.ceil(launched[m - 1] * day45Share(pct30, pct60)) *
          pricing.bonusDay45
        : 0
    // The day 90 count rounds to the nearest whole rep.
    const day90 =
      m >= 3 ? Math.round(cohortDay90[m - 3]) * pricing.bonusDay90 : 0
    return day15 + day45 + day90
  })

  // The leader earns the standard milestones on the same schedule as a rep
  // launched in their hire month: $500, then $1,000 the next month, then
  // $1,000 three months after launch.
  const leaderBonuses = Array.from({ length: months }, (_, m) => {
    if (!leaderCampaign || !inputs.pkg || inputs.salesSchoolEnabled === false)
      return 0
    if (m === leaderHireIdx) return pricing.bonusDay15
    if (m === leaderHireIdx + 1) return pricing.bonusDay45
    if (m === leaderHireIdx + 3) return pricing.bonusDay90
    return 0
  })
  // Bonuses are billed in arrears: whatever is earned in a month is invoiced
  // the following month.
  const bonusesWithLeader = Array.from({ length: months }, (_, m) =>
    m >= 1 ? bonuses[m - 1] + leaderBonuses[m - 1] : 0,
  )

  // Monthly build fees - use custom if set, otherwise default to 0 for all months
  const buildFees = Array(months)
    .fill(0)
    .map((_, i) => {
      // Check for custom build fee (index i+1 because index 0 is Build row)
      if (
        customBuildFees &&
        customBuildFees[i + 1] !== undefined &&
        customBuildFees[i + 1] !== null
      ) {
        return customBuildFees[i + 1]
      }
      // Default: no build fees in monthly rows
      return 0
    })

  // Partnership fee - set to 0 when there is no ad spend that month.
  // Leader search months bill the flat leader partnership fee instead.
  const platformFees = effectiveCampaigns.map((campaigns, idx) => {
    if (isLeaderMonth(idx))
      return inputs.pkg ? pricing.leaderPartnershipFee : 0
    return campaigns === 0 ? 0 : systemMonthly
  })

  // On the monthly schedule, the first month's fees are collected up front in
  // the Build column instead of Month 1.
  const prepayFirstMonth =
    !!inputs.pkg && pricing.firstMonthInBuild && months > 0
  const buildPlatformFee = prepayFirstMonth ? platformFees[0] : 0
  const buildCampaignFee = prepayFirstMonth ? campaignFees[0] : 0
  const buildRecruiterFee = prepayFirstMonth ? recruiterFees[0] : 0
  if (prepayFirstMonth) {
    platformFees[0] = 0
    campaignFees[0] = 0
    recruiterFees[0] = 0
  }
  const buildRowTotal =
    buildRowFee + buildPlatformFee + buildCampaignFee + buildRecruiterFee

  const hireCost = Array(months)
    .fill(0)
    .map(
      (_, i) =>
        buildFees[i] + platformFees[i] + campaignFees[i] + recruiterFees[i],
    )

  const adSpend = effectiveCampaigns.map((c, idx) =>
    isLeaderMonth(idx)
      ? getLeaderAdSpend(inputs, idx)
      : effectiveAdSpendPerCampaign[idx] * c,
  )

  const totalCost = Array(months)
    .fill(0)
    .map((_, i) => hireCost[i] + adSpend[i] + bonusesWithLeader[i])
  const netProfit = grossProfit.map((x, i) => x - totalCost[i])

  const rows: RowData[] = []
  let cum = -buildRowTotal

  rows.push({
    period: 'Implementation',
    activeCampaigns: 0,
    hires: 0,
    launched: 0,
    active30d: 0,
    active60d: 0,
    active90d: 0,
    veterans: 0,
    rampingReps: 0,
    rampingSales: 0,
    veteranReps: 0,
    buildFee: buildRowFee,
    platformFee: buildPlatformFee,
    campaignFee: buildCampaignFee,
    recruiterFee: buildRecruiterFee,
    leaderRevShare: 0,
    hireCost: buildRowTotal,
    adSpend: 0,
    bonuses: 0,
    totalCost: buildRowTotal,
    totalSales: 0,
    realizationRate: pullthrough,
    grossRevenue: 0,
    grossProfit: 0,
    netProfit: -buildRowTotal,
    cumProfit: cum,
    residualRevenue: 0,
    totalResidualRevenue: 0,
  })

  for (let i = 0; i < months; i++) {
    cum += netProfit[i]
    // Trail months carry no team: only year-one customers keep paying.
    const team = isTrailMonth(i) ? 0 : 1
    rows.push({
      period: periodLabel(i, leaderMonths),
      activeCampaigns: effectiveCampaigns[i],
      hires: hired[i],
      launched: (launched[i] + leaderLaunched[i]) * team,
      // Stacked summer launch: everyone starts on day one, so the month's
      // active reps equal the reps who earn the day-15 bonus.
      ...(stackAtLaunch && i === launchIdx && launched[i] > 0
        ? {
            launchWeight:
              Math.ceil(launched[i] * day15Share(pct30)) /
              (launched[i] + leaderLaunched[i]),
          }
        : {}),
      active30d: (active30d[i] + leaderActive30d[i]) * team,
      active60d: (active60d[i] + leaderActive60d[i]) * team,
      active90d: (active90d[i] + leaderActive90d[i]) * team,
      veterans: (veterans[i] + leaderVeterans[i]) * team,
      rampingReps: rampingReps[i] * team,
      rampingSales: rampingSales[i] * team,
      veteranReps: veteranReps[i] * team,
      buildFee: buildFees[i],
      platformFee: platformFees[i],
      campaignFee: campaignFees[i],
      recruiterFee: recruiterFees[i],
      leaderRevShare: 0,
      hireCost: hireCost[i],
      adSpend: adSpend[i],
      bonuses: bonusesWithLeader[i],
      totalCost: totalCost[i],
      totalSales: salesShown[i],
      realizationRate: pullthrough,
      grossRevenue: revenueShown[i],
      grossProfit: grossProfit[i],
      netProfit: netProfit[i],
      cumProfit: cum,
      commissions: commissions.net[i],
      commissionUpfront: commissions.upfront[i],
      commissionResidual: commissions.residual[i],
      commissionClawback: commissions.clawbacks[i],
      commissionBackend: commissions.backend[i],
      overrideUpfront: overrides?.upfront[i],
      overrideBackend: overrides?.backend[i],
      overrideClawback: overrides?.clawbacks[i],
      residualRevenue: residualShown[i],
      totalResidualRevenue: totalResidualShown[i],
    })
  }

  const totalHireCost = buildRowTotal + hireCost.reduce((a, b) => a + b, 0)
  const totalAdSpend = adSpend.reduce((a, b) => a + b, 0)
  const totalBonuses = bonusesWithLeader.reduce((a, b) => a + b, 0)
  const totalCostAll = totalHireCost + totalAdSpend + totalBonuses
  const totalGrossProfit = grossProfit.reduce((a, b) => a + b, 0)
  const totalNetProfit =
    totalGrossProfit -
    (hireCost.reduce((a, b) => a + b, 0) + totalAdSpend + totalBonuses) -
    buildRowTotal
  const roi = totalCostAll > 0 ? (totalNetProfit / totalCostAll) * 100 : 0

  let breakeven = `Not within ${months} months`
  for (let i = 0; i < rows.length; i++) {
    if (rows[i].cumProfit >= 0) {
      breakeven = rows[i].period
      break
    }
  }

  return {
    preset,
    hiresPerMonth: hired[0],
    launchedPerMonth: launched[0],
    gpPerSale,
    totalHireCost,
    totalAdSpend,
    totalBonuses,
    totalCostAll,
    totalGrossProfit,
    totalNetProfit,
    roi,
    breakeven,
    rows,
  }
}

/** Months revenue is pushed back (0–11). */
export function getRevenueDelay(inputs: Inputs): number {
  const value = Math.round(Number(inputs.revenueDelayMonths) || 0)
  return Math.min(11, Math.max(0, value))
}

// Active reps for a month, matching the projection table's
// "Active Reps (Average)" metric.
export function activeRepsForRow(row: RowData): number {
  return (
    row.launched * (row.launchWeight ?? 0.5) +
    row.active30d +
    row.active60d +
    row.active90d +
    row.veterans
  )
}

function generateCampaignPatterns(): number[][] {
  const candidates: number[][] = []

  // 1. Flat patterns — extended to a high ceiling so large targets are always
  // reachable by simply running more campaigns each month.
  const MAX_CAMPAIGNS = 40
  for (let n = 1; n <= MAX_CAMPAIGNS; n += 1) {
    candidates.push(Array(12).fill(n))
  }

  // 1b. Front-load then taper, scaled up to the high ceiling (for cheaper ways
  // to hit large targets than a flat high pattern).
  for (let peak = 6; peak <= MAX_CAMPAIGNS; peak += 1) {
    for (const plateauMonths of [2, 3, 4, 6]) {
      const pattern: number[] = []
      for (let m = 0; m < 12; m++) {
        if (m < plateauMonths) {
          pattern.push(peak)
        } else {
          const tapered = peak - (m - plateauMonths + 1)
          pattern.push(Math.max(1, tapered))
        }
      }
      candidates.push(pattern)
    }
  }

  // 2. Ramp-up then plateau
  for (let start = 1; start <= 3; start += 1) {
    for (let peak = start; peak <= 5; peak += 1) {
      if (peak === start) continue
      for (let rampMonth = 1; rampMonth <= 6; rampMonth++) {
        const pattern: number[] = []
        for (let m = 0; m < 12; m++) {
          pattern.push(m < rampMonth ? start : peak)
        }
        candidates.push(pattern)
      }
    }
  }

  // 3. Front-load then taper down to the 1x floor
  for (let peak = 2; peak <= 5; peak += 1) {
    for (let plateauMonths = 1; plateauMonths <= 9; plateauMonths++) {
      const pattern: number[] = []
      for (let m = 0; m < 12; m++) {
        if (m < plateauMonths) {
          pattern.push(peak)
        } else {
          const tapered = peak - (m - plateauMonths + 1)
          pattern.push(Math.max(1, tapered))
        }
      }
      candidates.push(pattern)
    }
  }

  // 4. Gradual ramp in whole campaign increments
  for (let start = 1; start <= 2; start += 1) {
    for (let stepMonths = 1; stepMonths <= 3; stepMonths++) {
      for (let cap = start + 1; cap <= 5; cap += 1) {
        const pattern: number[] = []
        let cur = start
        for (let m = 0; m < 12; m++) {
          pattern.push(Math.min(cap, cur))
          if ((m + 1) % stepMonths === 0) cur = Math.min(cap, cur + 1)
        }
        candidates.push(pattern)
      }
    }
  }

  return candidates
}

export function optimizeCampaignsForRevenueTarget(
  inputs: Inputs,
  target: number,
  months: number = 12,
): number[] | null {
  if (!target || target <= 0) return null
  const horizon = Math.max(1, Math.min(12, months))
  const candidates = generateCampaignPatterns()

  // Cheapest pattern that MEETS or exceeds the target within the horizon
  let best: { pattern: number[]; cost: number } | null = null
  // Fallback: pattern producing the MOST revenue in the horizon (used when the
  // target can't be reached in the window, e.g. a short 3/6-month horizon)
  let maxRev: { pattern: number[]; revenue: number } | null = null

  for (const candidate of candidates) {
    // Constraint: first month must be >= 1.0 (no half-campaign start)
    if (candidate[0] < 1.0) continue

    const result = calculateModel({
      ...inputs,
      monthlyCampaigns: candidate,
      campaigns: candidate[0],
    })

    let cumGrossRevenue = 0
    let cumTotalCost = 0
    for (let i = 0; i <= horizon; i++) {
      cumGrossRevenue += result.rows[i].grossRevenue
      cumTotalCost += result.rows[i].totalCost
    }

    if (cumGrossRevenue >= target) {
      if (!best || cumTotalCost < best.cost) {
        best = { pattern: candidate, cost: cumTotalCost }
      }
    }

    if (!maxRev || cumGrossRevenue > maxRev.revenue) {
      maxRev = { pattern: candidate, revenue: cumGrossRevenue }
    }
  }

  // Prefer the cheapest pattern that hits the target; otherwise get as close as
  // possible (the highest-revenue pattern achievable for this horizon).
  if (best) return best.pattern
  return maxRev ? maxRev.pattern : null
}

// Finds the cheapest campaign pattern that reaches a target active-rep count
// (net team growth) in the final month of the horizon.
export function optimizeCampaignsForTeamTarget(
  inputs: Inputs,
  target: number,
  months: number = 12,
): number[] | null {
  if (!target || target <= 0) return null
  const horizon = Math.max(1, Math.min(12, months))
  const candidates = generateCampaignPatterns()

  let best: { pattern: number[]; cost: number } | null = null
  let maxTeam: { pattern: number[]; team: number } | null = null

  for (const candidate of candidates) {
    if (candidate[0] < 1.0) continue

    const result = calculateModel({
      ...inputs,
      monthlyCampaigns: candidate,
      campaigns: candidate[0],
    })

    const activeReps = activeRepsForRow(result.rows[horizon])

    let cumTotalCost = 0
    for (let i = 0; i <= horizon; i++) {
      cumTotalCost += result.rows[i].totalCost
    }

    if (activeReps >= target) {
      if (!best || cumTotalCost < best.cost) {
        best = { pattern: candidate, cost: cumTotalCost }
      }
    }

    if (!maxTeam || activeReps > maxTeam.team) {
      maxTeam = { pattern: candidate, team: activeReps }
    }
  }

  if (best) return best.pattern
  return maxTeam ? maxTeam.pattern : null
}
